import _ from "lodash";

const DEFAULT_CAMERA_DURATION_MS = 2500;
const DEFAULT_FADE_DURATION_MS = 500;
const DEFAULT_CAMERA_FOCUS_PADDING_X = 75;
const DEFAULT_CAMERA_FOCUS_PADDING_Y = 150;
const START_ZOOMED_CAMERA_START_MS = 50;
const START_ZOOMED_CAMERA_DURATION_MS = 150;

function toNumber(value, fallback = 0) {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? numericValue : fallback;
}

export function clamp01(value) {
	return Math.min(Math.max(toNumber(value, 0), 0), 1);
}

export function lerp(startValue, endValue, progress) {
	return startValue + ((endValue - startValue) * progress);
}

export function power3InOut(value) {
	const progress = clamp01(value);
	return progress < 0.5
		? 4 * progress * progress * progress
		: 1 - (Math.pow((-2 * progress) + 2, 3) / 2);
}

export function normalizeToArray(value) {
	if (Array.isArray(value)) {
		return value;
	}

	if (_.isPlainObject(value)) {
		return [value];
	}

	return [];
}

function hasConfiguredHotspots(item = {}) {
	return Array.isArray(item.imageHotspots) && item.imageHotspots.length > 0;
}

function hasConfiguredAdvancedCamera(item = {}) {
	if (Array.isArray(item.imageKenBurnsEffect)) {
		return item.imageKenBurnsEffect.length > 0;
	}

	return _.isPlainObject(item.imageKenBurnsEffect);
}

function hasSimpleKenBurns(item = {}) {
	return item.imageKenBurnsEffectEnabled === true
		|| item.imageKenBurnsEffectEnabled === "true"
		|| item.imageKenBurnsEffectEnabled === 1
		|| item.imageKenBurnsEffectEnabled === "1";
}

export function getArrowPoints(coords = {}) {
	const x = toNumber(coords.x, 0);
	const y = toNumber(coords.y, 0);
	const width = toNumber(coords.width, 0);
	const height = toNumber(coords.height, 0);

	return `${x} ${y + (height * 0.33)},${x + (width * 0.5)} ${y + (height * 0.33)},${x + (width * 0.5)} ${y},${x + width} ${y + (height * 0.5)},${x + (width * 0.5)} ${y + height},${x + (width * 0.5)} ${y + (height * 0.67)},${x} ${y + (height * 0.67)},${x} ${y + (height * 0.33)}`;
}

function getSceneMode(item = {}) {
	const hasHotspots = hasConfiguredHotspots(item);
	const hasAdvancedCamera = hasConfiguredAdvancedCamera(item) && !hasSimpleKenBurns(item);
	const hasSimpleKenBurnsEffect = hasSimpleKenBurns(item);

	if (hasHotspots || hasAdvancedCamera || item.imageShowInBackground) {
		return "svg-advanced";
	}

	if (hasSimpleKenBurnsEffect) {
		return "image-ken-burns";
	}

	return "image-static";
}

function getDefaultCameraState(imageSize = {}) {
	return {
		x: 0,
		y: 0,
		width: toNumber(imageSize.width, 0),
		height: toNumber(imageSize.height, 0),
		rotation: 0
	};
}

function getCameraStateFromEffect(effect, hotspots, hotspotIndex, imageSize, fallbackState) {
	const currentFallbackState = fallbackState || getDefaultCameraState(imageSize);
	const endCamera = _.get(effect, "camera.end", null);

	if (endCamera) {
		return {
			x: toNumber(endCamera.x, currentFallbackState.x),
			y: toNumber(endCamera.y, currentFallbackState.y),
			width: toNumber(endCamera.width, currentFallbackState.width),
			height: toNumber(endCamera.height, currentFallbackState.height),
			rotation: toNumber(endCamera.rotation, currentFallbackState.rotation)
		};
	}

	if (Array.isArray(hotspots) && hotspots[hotspotIndex] && hotspots[hotspotIndex].coords) {
		const coords = hotspots[hotspotIndex].coords;
		return {
			x: toNumber(coords.x, 0) - DEFAULT_CAMERA_FOCUS_PADDING_X,
			y: toNumber(coords.y, 0) - DEFAULT_CAMERA_FOCUS_PADDING_Y,
			width: toNumber(coords.width, currentFallbackState.width) + (DEFAULT_CAMERA_FOCUS_PADDING_X * 2),
			height: toNumber(coords.height, currentFallbackState.height) + (DEFAULT_CAMERA_FOCUS_PADDING_Y * 2),
			rotation: currentFallbackState.rotation
		};
	}

	return currentFallbackState;
}

function getInitialCameraState(item, imageSize) {
	const initialState = getDefaultCameraState(imageSize);
	const effects = normalizeToArray(item.imageKenBurnsEffect);
	const firstEffect = effects.length > 0 ? effects[0] : null;
	const startZoomed = _.get(firstEffect, "camera.startZoomed", false) === true;

	if (!firstEffect || !startZoomed) {
		return initialState;
	}

	return getCameraStateFromEffect(firstEffect, item.imageHotspots, 0, imageSize, initialState);
}

function interpolateCameraState(startState, endState, progress) {
	const easedProgress = power3InOut(progress);

	return {
		x: lerp(startState.x, endState.x, easedProgress),
		y: lerp(startState.y, endState.y, easedProgress),
		width: lerp(startState.width, endState.width, easedProgress),
		height: lerp(startState.height, endState.height, easedProgress),
		rotation: lerp(startState.rotation, endState.rotation, easedProgress)
	};
}

function normalizeCameraEffects(item) {
	return normalizeToArray(item.imageKenBurnsEffect)
		.filter((effect) => _.isPlainObject(effect))
		.map((effect, originalIndex) => {
			const startZoomed = _.get(effect, "camera.startZoomed", false) === true;
			const authoredStartTime = toNumber(effect.startTime, 2000);
			const authoredDuration = toNumber(effect.duration, DEFAULT_CAMERA_DURATION_MS);
			const authoredEndTime = effect.endTime !== undefined && effect.endTime !== null
				? toNumber(effect.endTime, authoredStartTime + authoredDuration)
				: authoredStartTime + authoredDuration;
			const startTime = startZoomed ? START_ZOOMED_CAMERA_START_MS : authoredStartTime;
			const endTime = startZoomed
				? startTime + START_ZOOMED_CAMERA_DURATION_MS
				: authoredEndTime;

			return {
				effect,
				index: originalIndex,
				originalIndex,
				startTime,
				endTime,
				duration: Math.max(1, endTime - startTime),
				targetState: null,
				startState: null
			};
		})
		.sort((a, b) => {
			if (a.startTime !== b.startTime) {
				return a.startTime - b.startTime;
			}

			return a.originalIndex - b.originalIndex;
		});
}

function evaluateCameraTimelineState(segments, timeMs, initialState) {
	let currentState = _.cloneDeep(initialState);

	for (let index = 0; index < segments.length; index += 1) {
		const segment = segments[index];
		if (timeMs < segment.startTime) {
			break;
		}

		if (timeMs >= segment.endTime) {
			currentState = segment.targetState;
			continue;
		}

		currentState = interpolateCameraState(
			segment.startState,
			segment.targetState,
			(timeMs - segment.startTime) / segment.duration
		);
	}

	return currentState;
}

function buildResolvedCameraTimeline(item, imageSize) {
	const initialState = getInitialCameraState(item, imageSize);
	const segments = normalizeCameraEffects(item);

	segments.forEach((segment, index) => {
		const priorSegments = segments.slice(0, index);
		const startState = evaluateCameraTimelineState(priorSegments, segment.startTime, initialState);
		const targetState = getCameraStateFromEffect(
			segment.effect,
			item.imageHotspots,
			segment.index,
			imageSize,
			startState
		);

		segment.startState = startState;
		segment.targetState = targetState;
	});

	return {
		initialState,
		segments
	};
}

function evaluateCameraState(item, timeMs, imageSize) {
	const sceneMode = getSceneMode(item);
	if (sceneMode !== "svg-advanced") {
		return getDefaultCameraState(imageSize);
	}

	const { initialState, segments } = buildResolvedCameraTimeline(item, imageSize);
	if (segments.length === 0) {
		return getDefaultCameraState(imageSize);
	}

	return evaluateCameraTimelineState(segments, timeMs, initialState);
}

function evaluateSimpleKenBurnsState(item, timeMs) {
	const durationMs = item.imageKenBurnsEffectDuration && item.imageKenBurnsEffectDuration !== "default"
		? toNumber(item.imageKenBurnsEffectDuration, 1000)
		: 1000;
	const progress = clamp01(timeMs / Math.max(1, durationMs));
	const easedProgress = power3InOut(progress);

	const startScaleX = item.imageKenBurnsEffectStartScaleX && item.imageKenBurnsEffectStartScaleX !== "default"
		? toNumber(item.imageKenBurnsEffectStartScaleX, 1)
		: 1;
	const startScaleY = item.imageKenBurnsEffectStartScaleY && item.imageKenBurnsEffectStartScaleY !== "default"
		? toNumber(item.imageKenBurnsEffectStartScaleY, 1)
		: 1;
	const endScaleX = item.imageKenBurnsEffectEndScaleX && item.imageKenBurnsEffectEndScaleX !== "default"
		? toNumber(item.imageKenBurnsEffectEndScaleX, 1.3)
		: 1.3;
	const endScaleY = item.imageKenBurnsEffectEndScaleY && item.imageKenBurnsEffectEndScaleY !== "default"
		? toNumber(item.imageKenBurnsEffectEndScaleY, 1.3)
		: 1.3;

	return {
		scaleX: lerp(startScaleX, endScaleX, easedProgress),
		scaleY: lerp(startScaleY, endScaleY, easedProgress)
	};
}

function evaluateHotspotOpacity(hotspot, timeMs) {
	const startTime = toNumber(hotspot.startTime, 0);
	const startDuration = _.get(hotspot, "startTimeTransition.duration", DEFAULT_FADE_DURATION_MS);
	const startFromOpacity = _.get(hotspot, "startTimeTransition.fromOpacity", 0);
	const startToOpacity = _.get(hotspot, "startTimeTransition.toOpacity", 1);

	if (timeMs < startTime) {
		return 0;
	}

	let opacity;
	if (startDuration > 0 && timeMs < startTime + startDuration) {
		opacity = lerp(startFromOpacity, startToOpacity, (timeMs - startTime) / startDuration);
	} else {
		opacity = startToOpacity;
	}

	const endTime = hotspot.endTime !== undefined && hotspot.endTime !== null
		? toNumber(hotspot.endTime, -1)
		: -1;

	if (endTime < 0) {
		return opacity;
	}

	const endDuration = _.get(hotspot, "endTimeTransition.duration", DEFAULT_FADE_DURATION_MS);
	const endToOpacity = _.get(hotspot, "endTimeTransition.toOpacity", 0);

	if (timeMs < endTime) {
		return opacity;
	}

	if (endDuration > 0 && timeMs < endTime + endDuration) {
		return lerp(opacity, endToOpacity, (timeMs - endTime) / endDuration);
	}

	return endToOpacity;
}

function evaluateHotspots(item, timeMs) {
	return normalizeToArray(item.imageHotspots).map((hotspot) => {
		const opacity = evaluateHotspotOpacity(hotspot, timeMs);
		const coords = hotspot.coords || {};
		const centerX = toNumber(coords.x, 0) + (toNumber(coords.width, 0) * 0.5);
		const centerY = toNumber(coords.y, 0) + (toNumber(coords.height, 0) * 0.5);

		return {
			id: hotspot.id || hotspot.guid,
			guid: hotspot.guid,
			shape: hotspot.shape || "rectangle",
			coords: {
				x: toNumber(coords.x, 0),
				y: toNumber(coords.y, 0),
				width: toNumber(coords.width, 0),
				height: toNumber(coords.height, 0)
			},
			centerX,
			centerY,
			rotation: toNumber(hotspot.rotation, 0),
			opacity,
			visible: opacity > 0,
			style: {
				borderRadius: _.get(hotspot, "style.borderRadius", 0),
				borderColor: _.get(hotspot, "style.borderColor", "darkorange"),
				borderWidth: _.get(hotspot, "style.borderWidth", 5),
				borderFillColor: _.get(hotspot, "style.borderFillColor", "transparent")
			},
			points: getArrowPoints(coords),
			action: hotspot.action || "none",
			label: hotspot.label || ""
		};
	}).filter((hotspot) => hotspot.visible);
}

export function getScheduledHotspotGuidsAtTime(item = {}, timeMs = 0) {
	return normalizeToArray(item.imageHotspots)
		.filter((hotspot) => {
			const startTime = toNumber(_.get(hotspot, "startTime", 0), 0);
			if (timeMs < startTime) {
				return false;
			}

			const endTime = hotspot.endTime !== undefined && hotspot.endTime !== null
				? toNumber(hotspot.endTime, -1)
				: -1;
			if (endTime < 0) {
				return true;
			}

			const endDuration = toNumber(_.get(hotspot, "endTimeTransition.duration", DEFAULT_FADE_DURATION_MS), DEFAULT_FADE_DURATION_MS);
			return timeMs <= (endTime + Math.max(0, endDuration));
		})
		.map((hotspot) => hotspot.guid)
		.filter(Boolean);
}

export function getRelevantCameraGuidsAtTime(item = {}, timeMs = 0, imageSize = { width: 0, height: 0 }) {
	const sceneMode = getSceneMode(item);
	if (sceneMode !== "svg-advanced") {
		return [];
	}

	const { segments } = buildResolvedCameraTimeline(item, imageSize);
	if (segments.length === 0) {
		return [];
	}

	let activeSegment = null;
	let lastCompletedSegment = null;

	for (let index = 0; index < segments.length; index += 1) {
		const segment = segments[index];
		if (timeMs < segment.startTime) {
			break;
		}

		if (timeMs >= segment.endTime) {
			lastCompletedSegment = segment;
			continue;
		}

		activeSegment = segment;
	}

	const fallbackSegment = _.get(segments, "[0].effect.camera.startZoomed", false) === true
		? segments[0]
		: null;
	const relevantSegment = activeSegment || lastCompletedSegment || fallbackSegment;
	if (!relevantSegment || !relevantSegment.effect) {
		return [];
	}

	const guid = relevantSegment.effect.guid || relevantSegment.effect.id || "";
	return guid ? [guid] : [];
}

export function evaluateImageSceneAtTime(params = {}) {
	const item = params.item || {};
	const imageSize = params.imageSize || { width: 0, height: 0 };
	const timeMs = toNumber(params.timeMs, 0);
	const sceneMode = getSceneMode(item);

	return {
		sceneMode,
		hasHotspots: hasConfiguredHotspots(item),
		hasAdvancedCamera: hasConfiguredAdvancedCamera(item) && !hasSimpleKenBurns(item),
		hasSimpleKenBurnsEffect: hasSimpleKenBurns(item),
		cameraState: evaluateCameraState(item, timeMs, imageSize),
		simpleKenBurnsState: evaluateSimpleKenBurnsState(item, timeMs),
		hotspots: evaluateHotspots(item, timeMs)
	};
}

export function estimateImageSceneDurationMs(item = {}, audioDurationMs = 0) {
	const candidates = [toNumber(audioDurationMs, 0)];

	normalizeToArray(item.imageHotspots).forEach((hotspot) => {
		const startTime = toNumber(_.get(hotspot, "startTime", 0), 0);
		const endTime = hotspot.endTime !== undefined && hotspot.endTime !== null
			? toNumber(hotspot.endTime, -1)
			: -1;
		const fadeInDuration = toNumber(_.get(hotspot, "startTimeTransition.duration", DEFAULT_FADE_DURATION_MS), DEFAULT_FADE_DURATION_MS);
		const fadeOutDuration = toNumber(_.get(hotspot, "endTimeTransition.duration", DEFAULT_FADE_DURATION_MS), DEFAULT_FADE_DURATION_MS);

		candidates.push(startTime + Math.max(0, fadeInDuration));
		if (endTime >= 0) {
			candidates.push(endTime + Math.max(0, fadeOutDuration));
		}
	});

	normalizeToArray(item.imageKenBurnsEffect).forEach((effect) => {
		const startZoomed = _.get(effect, "camera.startZoomed", false) === true;
		const startTime = startZoomed
			? START_ZOOMED_CAMERA_START_MS
			: toNumber(_.get(effect, "startTime", 2000), 2000);
		const duration = startZoomed
			? START_ZOOMED_CAMERA_DURATION_MS
			: toNumber(_.get(effect, "duration", DEFAULT_CAMERA_DURATION_MS), DEFAULT_CAMERA_DURATION_MS);
		const endTime = startZoomed
			? startTime + duration
			: (effect.endTime !== undefined && effect.endTime !== null
				? toNumber(effect.endTime, startTime + duration)
				: startTime + duration);

		candidates.push(startTime);
		candidates.push(endTime);
	});

	if (hasSimpleKenBurns(item)) {
		candidates.push(
			item.imageKenBurnsEffectDuration && item.imageKenBurnsEffectDuration !== "default"
				? toNumber(item.imageKenBurnsEffectDuration, 1000)
				: 1000
		);
	}

	const maxCandidate = Math.max(...candidates.filter((candidate) => Number.isFinite(candidate) && candidate >= 0), 0);
	return maxCandidate > 0 ? maxCandidate + 500 : 15000;
}
